/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import orderBy from 'lodash.orderby';
import { EntityStore } from 'src/core/types/EntityStore';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import {
    CustomTextModelEvaluationSwagger,
    MultiClassificationEvaluationSwagger,
    SingleClassificationEvaluationSwagger
} from 'src/customText/core/services/trainedModels/customTextModelEvaluationSwagger';
import { CustomTextModelVerificationSwagger } from 'src/customText/core/services/trainedModels/customTextModelVerificationSwagger';
import { CustomTextClassStore } from 'src/customText/core/stores/CustomTextClassStore';
import { CustomTextEntityLabelStore } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { CustomTextTrainedModelStore } from 'src/customText/core/stores/CustomTextTrainedModelStore';
import { CustomTextVerificationDocumentStore } from 'src/customText/core/stores/CustomTextVerificationDocumentStore';
import { ModelEvaluationStore, PerformanceMetricsBreakdown } from 'src/modules/modelDetails/stores/ModelEvaluationStore';
import {
    LegacyModelEvaluationConfusionMatrix,
    ModelEvaluationConfusionMatrix,
    ModelEvaluationConfusionMatrixValue
} from 'src/modules/modelEvaluation/modelEvaluationSwaggers';
import { TrainedModelSwagger } from 'src/modules/modelTraining/types/trainingSwaggers';

export const customTextTrainedModelsSwaggerToStores = (swagger: TrainedModelSwagger[]): CustomTextTrainedModelStore[] =>
    swagger.map(
        modelInfo =>
            new CustomTextTrainedModelStore(modelInfo.label, {
                id: modelInfo.modelId,
                lastTrainingDate: modelInfo.lastTrainedDateTime ? new Date(modelInfo.lastTrainedDateTime) : undefined,
                lastTrainingDuration: modelInfo.lastTrainingDurationInSeconds,
                expirationDate: modelInfo.modelExpirationDate ? new Date(modelInfo.modelExpirationDate) : undefined,
                trainingConfigVersion: modelInfo.modelTrainingConfigVersion,
                hasSnapshot: modelInfo.hasSnapshot ? modelInfo.hasSnapshot : false
            })
    );

export const customTextTrainedModelVerificationSwaggerToStore = (
    swagger: CustomTextModelVerificationSwagger[],
    tagsStores: EntityStore[] | CustomTextClassStore[]
): CustomTextVerificationDocumentStore[] => {
    const documentStores: CustomTextVerificationDocumentStore[] = [];

    swagger.forEach(modelVerificationData => {
        switch (modelVerificationData.projectKind) {
            case 'CustomHealthcare':
                modelVerificationData.customHealthcareResult.entities.forEach(entity => {
                    let entityLabels = entity.expectedEntities.map(label => {
                        const correspondingEntityStore = (<EntityStore[]>tagsStores).find(
                            entityStore => entityStore.name === label.category
                        );
                        if (correspondingEntityStore) {
                            return new CustomTextEntityLabelStore({
                                entityId: correspondingEntityStore.id,
                                startCharIndex: label.offset,
                                endCharIndex: label.offset + label.length
                            });
                        }
                    });

                    let entityPredictions = entity.predictedEntities.map(prediction => {
                        const correspondingEntityStore = (<EntityStore[]>tagsStores).find(
                            entityStore => entityStore.name === prediction.category
                        );
                        if (correspondingEntityStore) {
                            return new CustomTextEntityLabelStore({
                                entityId: correspondingEntityStore.id,
                                startCharIndex: prediction.offset,
                                endCharIndex: prediction.offset + prediction.length
                            });
                        }
                    });

                    entityLabels = entityLabels.filter(Boolean);
                    entityPredictions = entityPredictions.filter(Boolean);

                    documentStores.push(
                        new CustomTextVerificationDocumentStore({
                            entityLabels,
                            entityPredictions,
                            name: modelVerificationData.location,
                            language: modelVerificationData.language
                        })
                    );
                });

                break;
            case 'CustomEntityRecognition':
                modelVerificationData.customEntityRecognitionResult.entities.forEach(entity => {
                    let entityLabels = entity.expectedEntities.map(label => {
                        const correspondingEntityStore = (<EntityStore[]>tagsStores).find(
                            entityStore => entityStore.name === label.category
                        );
                        if (correspondingEntityStore) {
                            return new CustomTextEntityLabelStore({
                                entityId: correspondingEntityStore.id,
                                startCharIndex: label.offset,
                                endCharIndex: label.offset + label.length
                            });
                        }
                    });

                    let entityPredictions = entity.predictedEntities.map(prediction => {
                        const correspondingEntityStore = (<EntityStore[]>tagsStores).find(
                            entityStore => entityStore.name === prediction.category
                        );
                        if (correspondingEntityStore) {
                            return new CustomTextEntityLabelStore({
                                entityId: correspondingEntityStore.id,
                                startCharIndex: prediction.offset,
                                endCharIndex: prediction.offset + prediction.length
                            });
                        }
                    });
                    entityLabels = entityLabels.filter(Boolean);
                    entityPredictions = entityPredictions.filter(Boolean);

                    documentStores.push(
                        new CustomTextVerificationDocumentStore({
                            entityLabels,
                            entityPredictions,
                            name: modelVerificationData.location,
                            language: modelVerificationData.language
                        })
                    );
                });
                break;

            case 'CustomSingleLabelClassification':
                const classLabeled = (<CustomTextClassStore[]>tagsStores).find(
                    cS => cS.name === modelVerificationData.customSingleLabelClassificationResult.expectedClass
                );
                documentStores.push(
                    new CustomTextVerificationDocumentStore({
                        name: modelVerificationData.location,
                        language: modelVerificationData.language,
                        classLabels: classLabeled ? [classLabeled] : [],
                        classPredictions: [modelVerificationData.customSingleLabelClassificationResult.predictedClass]
                    })
                );
                break;

            case 'CustomMultiLabelClassification':
                const classLabels = modelVerificationData.customMultiLabelClassificationResult.expectedClasses
                    .map(cName => (<CustomTextClassStore[]>tagsStores).find(cS => cS.name === cName))
                    .filter(Boolean);

                documentStores.push(
                    new CustomTextVerificationDocumentStore({
                        classLabels,
                        name: modelVerificationData.location,
                        language: modelVerificationData.language,
                        classPredictions: modelVerificationData.customMultiLabelClassificationResult.predictedClasses
                    })
                );
                break;
            default:
        }
    });

    return documentStores;
};

export const customTextTrainedModelEvaluationSwaggerToModelEvaluationStore = (
    swagger: CustomTextModelEvaluationSwagger
): ModelEvaluationStore => {
    let f1Score: number;
    let recall: number;
    let precision: number;
    let confusionMatrix: number[][];
    let normalizedConfusionMatrix: number[][];
    let performanceMetricsBreakdown: PerformanceMetricsBreakdown[];

    const calculateConfusionMatrix = (
        isNormalized: boolean,
        extractionEntityNames: string[],
        _confusionMatrix: ModelEvaluationConfusionMatrix | LegacyModelEvaluationConfusionMatrix
    ) =>
        extractionEntityNames.map(entity => {
            const entityPredictionCount: number[] = [];

            extractionEntityNames.forEach(entityName => {
                /**
                 * @todo Remove this when backend solve APIM issues.
                 */
                const confusionMatrixSwagger = _confusionMatrix?.[entityName];
                const value =
                    (isNormalized
                        ? ((<ModelEvaluationConfusionMatrixValue>confusionMatrixSwagger)?.[entity]?.normalizedValue ?? 0) / 100.0
                        : (<ModelEvaluationConfusionMatrixValue>confusionMatrixSwagger)?.[entity]?.rawValue) ??
                    <number>confusionMatrixSwagger?.[entity] ??
                    0;

                entityPredictionCount.push(value);
            });

            return entityPredictionCount;
        });

    switch (swagger.projectKind) {
        case 'CustomHealthcare':
            const extractionHealthcareEntityNames = orderBy(Object.keys(swagger.customHealthcareEvaluation.entities));
            if (
                Object.keys(swagger.customHealthcareEvaluation.confusionMatrix).includes(
                    LuisConstants.CustomText.NoneLabelInConfusionMatrix
                )
            ) {
                extractionHealthcareEntityNames.splice(0, 0, LuisConstants.CustomText.NoneLabelInConfusionMatrix);
            }

            performanceMetricsBreakdown = extractionHealthcareEntityNames.map(key => ({
                name: key,
                f1Score: swagger.customHealthcareEvaluation.entities[key]?.f1,
                ...swagger.customHealthcareEvaluation.entities[key]
            }));

            confusionMatrix = calculateConfusionMatrix(
                false,
                extractionHealthcareEntityNames,
                swagger.customHealthcareEvaluation.confusionMatrix
            );
            normalizedConfusionMatrix = calculateConfusionMatrix(
                true,
                extractionHealthcareEntityNames,
                swagger.customHealthcareEvaluation.confusionMatrix
            );
            f1Score = swagger.customHealthcareEvaluation.microF1;
            recall = swagger.customHealthcareEvaluation.microRecall;
            precision = swagger.customHealthcareEvaluation.microPrecision;

            break;
        case 'CustomEntityRecognition':
            const extractionEntityNames = orderBy(Object.keys(swagger.customEntityRecognitionEvaluation.entities));
            if (
                Object.keys(swagger.customEntityRecognitionEvaluation.confusionMatrix).includes(
                    LuisConstants.CustomText.NoneLabelInConfusionMatrix
                )
            ) {
                extractionEntityNames.splice(0, 0, LuisConstants.CustomText.NoneLabelInConfusionMatrix);
            }

            performanceMetricsBreakdown = extractionEntityNames.map(key => ({
                name: key,
                f1Score: swagger.customEntityRecognitionEvaluation.entities[key]?.f1,
                ...swagger.customEntityRecognitionEvaluation.entities[key]
            }));

            confusionMatrix = calculateConfusionMatrix(
                false,
                extractionEntityNames,
                swagger.customEntityRecognitionEvaluation.confusionMatrix
            );
            normalizedConfusionMatrix = calculateConfusionMatrix(
                true,
                extractionEntityNames,
                swagger.customEntityRecognitionEvaluation.confusionMatrix
            );
            f1Score = swagger.customEntityRecognitionEvaluation.microF1;
            recall = swagger.customEntityRecognitionEvaluation.microRecall;
            precision = swagger.customEntityRecognitionEvaluation.microPrecision;

            break;
        case 'CustomSingleLabelClassification':
            const singleClassificationClassNames = orderBy(
                Object.keys((<SingleClassificationEvaluationSwagger>swagger).customSingleLabelClassificationEvaluation.classes)
            );

            if (
                Object.keys(swagger.customSingleLabelClassificationEvaluation.confusionMatrix).includes(
                    LuisConstants.CustomText.NoneLabelInConfusionMatrix
                )
            ) {
                singleClassificationClassNames.splice(0, 0, LuisConstants.CustomText.NoneLabelInConfusionMatrix);
            }

            performanceMetricsBreakdown = singleClassificationClassNames.map(key => ({
                name: key,
                f1Score: swagger.customSingleLabelClassificationEvaluation.classes[key].f1,
                ...swagger.customSingleLabelClassificationEvaluation.classes[key]
            }));

            confusionMatrix = calculateConfusionMatrix(
                false,
                singleClassificationClassNames,
                swagger.customSingleLabelClassificationEvaluation.confusionMatrix
            );
            normalizedConfusionMatrix = calculateConfusionMatrix(
                true,
                singleClassificationClassNames,
                swagger.customSingleLabelClassificationEvaluation.confusionMatrix
            );

            f1Score = swagger.customSingleLabelClassificationEvaluation.microF1;
            recall = swagger.customSingleLabelClassificationEvaluation.microRecall;
            precision = swagger.customSingleLabelClassificationEvaluation.microPrecision;

            break;
        case 'CustomMultiLabelClassification':
            const multiClassificationClassNames = Object.keys(
                (<MultiClassificationEvaluationSwagger>swagger).customMultiLabelClassificationEvaluation.classes
            );

            performanceMetricsBreakdown = multiClassificationClassNames.map(key => ({
                name: key,
                f1Score: swagger.customMultiLabelClassificationEvaluation.classes[key].f1,
                ...swagger.customMultiLabelClassificationEvaluation.classes[key]
            }));

            f1Score = swagger.customMultiLabelClassificationEvaluation.microF1;
            recall = swagger.customMultiLabelClassificationEvaluation.microRecall;
            precision = swagger.customMultiLabelClassificationEvaluation.microPrecision;

            break;
        default:
    }

    return new ModelEvaluationStore({
        recall,
        f1Score,
        precision,
        confusionMatrix,
        normalizedConfusionMatrix,
        performanceMetricsBreakdown,
        trainingSplitType: swagger.evaluationOptions?.kind,
        testingSplitPercentage: swagger.evaluationOptions?.testingSplitPercentage?.toString()
    });
};
